import type { CSSProperties } from 'react';
import { COLORS } from '../../constants/theme';

/**
 * SF-158: shared, non-component home for Relations' presentation constants/helpers — both
 * MobileRelationsShell/DesktopRelationsShell import from here rather than each independently
 * duplicating them (react-refresh/only-export-components blocks a value export from a component
 * file, same dragonPresentation.ts precedent). tabStyle/btnStyle are re-derived against the new
 * activeTab/busyAction-shaped props, not moved verbatim — the originals closed over Relations.tsx's
 * old isActing/declareTarget state, which this migration renames/splits.
 */

/** docs/GAME_MECHANICS.md "War System" / WarSystem.java:26-28. */
export const HOSTILITY_UNFRIENDLY_THRESHOLD = 5;
export const HOSTILITY_HOSTILE_THRESHOLD = 15;
export const HOSTILITY_CAP = 200;

export type HostilityTier = 'normal' | 'unfriendly' | 'hostile';

/**
 * Single source of truth for hostility-derived color/label — never derive from
 * RelationshipDto.relationship, confirmed dead/always-'NORMAL' in production (Phase 3 Decision 1).
 */
export function hostilityTier(points: number): HostilityTier {
  if (points >= HOSTILITY_HOSTILE_THRESHOLD) return 'hostile';
  if (points >= HOSTILITY_UNFRIENDLY_THRESHOLD) return 'unfriendly';
  return 'normal';
}

export const HOSTILITY_TIER_COLORS: Record<HostilityTier, string> = {
  normal: COLORS.text.tertiary,
  unfriendly: '#c9a84c',
  hostile: '#d4874a',
};

/** Reused "At War" red, already used elsewhere on this page (war header, ledger card border). */
export const AT_WAR_COLOR = '#c45a4a';
/** War-header card border — same value as btnStyle's 'danger' backgroundColor, centralized here
 *  so both shells reference one definition instead of duplicating the literal. */
export const WAR_BORDER_COLOR = '#7a2020';
/** Ceasefire status text — teal, distinct constant from AT_WAR_COLOR/WAR_BORDER_COLOR. */
export const CEASEFIRE_COLOR = '#6a9e8e';

/**
 * Denominator is 15 (the actionable War-declaration range), not the real 0-200 accumulation cap
 * (Phase 3 Decision 2) — values above 15 still clamp to a fully-filled bar; the raw number is
 * always shown as text alongside it so nothing is hidden.
 */
export function hostilityBarWidthPercent(value: number): number {
  const raw = (value / HOSTILITY_HOSTILE_THRESHOLD) * 100;
  const floor = value > 0 ? 3 : 0;
  return Math.min(100, Math.max(raw, floor));
}

export const CONFIRM_COPY: Record<
  'declareWar' | 'withdraw' | 'cancelCeasefire',
  { title: string; confirmText: string; cancelText: string }
> = {
  declareWar: { title: 'Declare War?', confirmText: 'Declare War', cancelText: 'Cancel' },
  withdraw: { title: 'Withdraw from War?', confirmText: 'Withdraw', cancelText: 'Cancel' },
  cancelCeasefire: { title: 'Cancel Ceasefire?', confirmText: 'Cancel Ceasefire', cancelText: 'Cancel' },
};

export function tabStyle(isActive: boolean): CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: isActive ? 600 : 400,
    color: isActive ? COLORS.text.primary : COLORS.text.tertiary,
    background: 'none',
    border: 'none',
    borderBottom: isActive ? `2px solid ${COLORS.link}` : '2px solid transparent',
    cursor: 'pointer',
  };
}

export function btnStyle(variant: 'primary' | 'danger' | 'secondary', disabled: boolean): CSSProperties {
  return {
    padding: '6px 14px',
    fontSize: 12,
    fontWeight: 600,
    border: 'none',
    borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    backgroundColor: variant === 'danger' ? WAR_BORDER_COLOR
      : variant === 'primary' ? '#2a4a3a'
      : COLORS.sectionHeader,
    color: variant === 'secondary' ? COLORS.text.secondary : COLORS.text.primary,
  };
}
